<template>
    <div v-if="firstEvent" class="tournamentCenterSportListEntrance TC_SportList_Entrance">
        <div
            class="tournamentCenterSportListEntranceCard TC_SportList_Entrance"
        >
            <picture class="tournamentCenterSportListEntrance-ctaBall">
                <source
                    srcset="@/assets/images/tournament-center/BgFlagBannerEntranceBallWorldCup2026.webp"
                    type="image/webp"
                >
                <img
                    src="@/assets/images/tournament-center/BgFlagBannerEntranceBallWorldCup2026.png"
                    alt=""
                    aria-hidden="true"
                >
            </picture>
            <div class="tournamentCenterSportListEntranceTitle">
                {{ i18n.t('tournament_center_event_title') }}
            </div>
            <div class="timeGroup">
                <template v-if="onLive">
                    <span class="liveBadge">{{ liveTranslation }}</span>
                    <span v-if="displayLiveScoreData && displayLiveScoreData.liveScoreInfoDetail && hasPeriod" class="time">{{ displayLiveScoreData.liveScoreInfoDetail }}</span>
                </template>
                <template v-else>
                    <span class="date">{{ firstEvent.eventDate }}</span>
                    <span class="time">{{ formatShowTime(firstEvent.showTime, firstEvent.showTimeType) }}</span>
                </template>
            </div>
            <div class="matchInfo">
                <div class="teamGroup">
                    <div class="teamSymbol">
                        <picture v-if="homeTeamLogo">
                            <source
                                v-bind:srcset="`${homeTeamLogo.webp}`"
                                type="image/webp"
                                class="animateImg"
                            >
                            <img
                                v-bind:src="`${homeTeamLogo.origin}`"
                                alt=""
                                aria-hidden="true"
                                class="teamSymbolImg"
                            >
                        </picture>
                    </div>
                    <div class="teamName" v-bind:class="{ _favorite: firstEvent.isHomeFavorite }">
                        {{ homeTeamName }}
                    </div>
                </div>
                <div class="teamGroup">
                    <div class="teamSymbol">
                        <picture v-if="awayTeamLogo">
                            <source
                                v-bind:srcset="`${awayTeamLogo.webp}`"
                                type="image/webp"
                                class="animateImg"
                            >
                            <img
                                v-bind:src="`${awayTeamLogo.origin}`"
                                alt=""
                                aria-hidden="true"
                                class="teamSymbolImg"
                            >
                        </picture>
                    </div>
                    <div class="teamName" v-bind:class="{ _favorite: firstEvent.isAwayFavorite }">
                        {{ awayTeamName }}
                    </div>
                </div>
            </div>
            <img
                class="tournamentCenterSportListEntrance-arrow"
                src="@/assets/images/multiple-sports/arrow-forward.svg"
                alt=""
                aria-hidden="true"
            >
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent } from 'vue';
    import useMatchInfoScore from '@/components/match/matchInfo/useMatchInfoScore';
    import { SportType } from '@/interface/enum';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { getTournamentTeamImage } from '@/libraries/helpers/tournamentCenterHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { useTournamentCenterEntranceEvents } from '@/libraries/oddsApi/composables/useTournamentCenterEntranceEventsQuery';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';

    export default defineComponent({
        name: 'TournamentCenterSportListEntrance',
        setup() {
            const { tournamentCenterMainLeagueIds } = storeToRefs(useFeatureToggleStore());

            const { events } = useTournamentCenterEntranceEvents({
                sportType: SportType.Soccer,
                isMixParlayMode: false,
                tournamentIds: tournamentCenterMainLeagueIds.value,
                isEnabled: true,
                isSubScriptionEnabled: false,
            });

            const firstEvent = computed(() => events.value[0]);

            const onLive = computed(() => firstEvent.value && firstEvent.value.isLive);

            const liveTranslation = computed(() => getTranslationBySite('live').toUpperCase());

            const { displayLiveScoreData } = useMatchInfoScore(firstEvent);

            const homeTeamLogo = computed(() => getTournamentTeamImage(firstEvent.value.homeTeam));
            const awayTeamLogo = computed(() => getTournamentTeamImage(firstEvent.value.awayTeam));

            const homeTeamName = computed(() => getTranslated(firstEvent.value.homeTeam?.teamName));
            const awayTeamName = computed(() => getTranslated(firstEvent.value.awayTeam?.teamName));

            const hasPeriod = computed(() => (firstEvent.value.mainMarketEventResult.extraInfo?.period ? firstEvent.value.mainMarketEventResult.extraInfo.period > 0 : false));

            return {
                i18n,
                firstEvent,
                onLive,
                liveTranslation,
                displayLiveScoreData,
                homeTeamLogo,
                awayTeamLogo,
                homeTeamName,
                awayTeamName,
                formatShowTime,
                hasPeriod,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/tournamentCenterSportListEntrance";
</style>
